import Typography from "@portal/components/atoms/v2/typography";
import useSavingProposal from "../../../../hooks/robot/useSavingProposal";
import { useEffect, useMemo } from "react";
import { MainBox } from "../components/mainBox";
import { SavingSuggestionChart } from "./helpers/SavingSuggestionChart";
import { MapFundsToList, MapFundsToSavingsAgreement } from "./helpers";
import { SavingSuggestionFunds } from "./components/SavingSuggestionFunds";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { RobotFooterComponent } from "../components/footer";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import Link from "next/link";
import Icon from "@portal/components/atoms/icons";
import PageSpinner from "@portal/components/molecules/v2/pageSpinner";
import { morningstarKeyInfoLink } from "@portal/helpers/morningstarKeyInfoLink";
import { useRobot } from "@portal/app/context/robotProvider";
import { IModifySavingsAgreementFund } from "@portal/types/api/IModifySavingsAgreementFund";
import { useCreateSavingsAgreement } from "@portal/app/context/createSavingsAgreementProvider";

export const SavingSuggestionPage = ({ centerId, pageIndex, isDefaultLanguage }: IPageProps) => {
  const { savingProposalRequest } = useRobot();
  const savingProposalResponse = useSavingProposal({
    ...savingProposalRequest,
    centerId,
  });
  const { request, setSavingsAgreementFunds } = useCreateSavingsAgreement();
  const isLoading = savingProposalResponse.isLoading;
  const data = savingProposalResponse.data?.funds;
  const funds = useMemo(() => MapFundsToList(savingProposalResponse), [savingProposalResponse]);
  const proposalAgreementFunds = useMemo(() => (data ? MapFundsToSavingsAgreement(data) : undefined), [data]);
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;

  useEffect(() => {
    if (!proposalAgreementFunds) {
      return;
    }

    const currentFunds = request.agreementData.funds as IModifySavingsAgreementFund[] | undefined;
    const isSameFunds =
      currentFunds &&
      currentFunds.length === proposalAgreementFunds.length &&
      currentFunds.every((fund, index) => {
        const nextFund = proposalAgreementFunds[index];
        return !!nextFund && fund.isin === nextFund.isin && fund.amount === nextFund.amount;
      });

    if (!isSameFunds) {
      setSavingsAgreementFunds(proposalAgreementFunds);
    }
  }, [proposalAgreementFunds, request.agreementData.funds, setSavingsAgreementFunds]);

  return (
    <>
      <MainBox>
        <div className="mb-5">
          <Typography>{texts.PAGE_14_INGRESS}</Typography>
        </div>
        {!isLoading && (
          <div className="flex flex-col mb-2 border dark:border-blueBorderDark rounded-md w-full p-1 bg-pageBoxBackgroundLight dark:bg-pageBoxBackgroundDark h-100 ">
            {isLoading ? <PageSpinner /> : <SavingSuggestionChart propositionFunds={data} />}
          </div>
        )}
        {data && data.length > 0 && <Typography variant="Bold">{texts.PAGE_14_KIID_HEADER}</Typography>}
        {data?.map(fund => (
          <div key={fund.isin} className="flex">
            <Link
              className="flex items-center space-x-1"
              target="_blank"
              href={morningstarKeyInfoLink(fund.isin)}
              prefetch={false}
            >
              <Icon name={"paperClipIcon"} size="xxsmall" />
              <Typography variant="BodySpan">{fund.name}</Typography>
            </Link>
          </div>
        ))}

        <div className="flex flex-col my-2 w-full">
          <Typography variant="Body">{texts.PAGE_14_INFO_TEXT}</Typography>
        </div>
        <div className="flex flex-col my-2">
          <Typography variant="H6">{texts.PAGE_14_PORTFOLIO}</Typography>
          <TextLine />
        </div>
        <SavingSuggestionFunds isDefaultLanguage={isDefaultLanguage} funds={funds} isLoading={isLoading} />
      </MainBox>
      <RobotFooterComponent
        pageIndex={pageIndex}
        centerId={centerId}
        primaryButtonText={texts.BUTTON_NEXT}
        secondaryButtonText={texts.BUTTON_BACK}
        disabled={isLoading}
      />
    </>
  );
};
